import { useEffect, useRef } from 'react'
import Gleap from 'gleap'
import { useAuth, useOrganization, useUser } from '@clerk/clerk-react'

import { useGetAccountInfo } from '@/lib/api/account'
import { useGetGleapUserHash, useSupportToolReaders } from '@/lib/api/gleap'
import { config } from '@/lib/config'
import { registerAgentTools } from '@/lib/gleap/agent-tools'
import { registerCaseLinkAction } from '@/lib/gleap/case-link-action'
import { identifyWhenVerified } from '@/lib/gleap/identify'
import { isTagadaPay } from '@/config/branding'

interface UseGleapChatProps {
	apiKey: string
}

/**
 * Hook to initialize and configure Gleap chat with user and organization data
 */
export function useGleapChat({ apiKey }: UseGleapChatProps) {
	const isInitialized = useRef(false)
	const { organization } = useOrganization()
	const { user } = useUser()
	const { data: accountInfo } = useGetAccountInfo();
	// Identity-verification hash. The Gleap project enforces it: an identify without
	// a valid hash is answered 401, so identify waits for it (see below).
	const { data: gleapHash } = useGetGleapUserHash();
	const { getToken } = useAuth()
	const supportReaders = useSupportToolReaders()
	// Agent tools are registered with Gleap once: their handlers read the current session through this ref.
	const session = useRef({ getToken, ...supportReaders })
	session.current = { getToken, ...supportReaders }
	// Same for the custom action: registered once, the account loads later and a sign-out leaves Gleap initialised.
	const caseAccountId = useRef<string | undefined>(undefined)
	caseAccountId.current = user ? accountInfo?.id : undefined

	useEffect(() => {
		// Skip Gleap in local development
		const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
		if (isLocalhost) {
			return
		}

		if (!apiKey) {
			console.error('Gleap chat requires an API key')
			return
		}
		if (!isTagadaPay) {
			return;
		}

		if (!user) {
			return
		}

		if (!isInitialized.current) {
			Gleap.initialize(apiKey)
			registerAgentTools(
				{
					getToken: () => session.current.getToken(),
					paymentStackUrl: config.paymentStackApiUrl,
					fetch: (input, init) => window.fetch(input, init),
					readBilling: (input) => session.current.readBilling(input),
					readStores: (input) => session.current.readStores(input),
				},
				(name, handler) => Gleap.registerAgentTool(name, handler),
			)
			registerCaseLinkAction(
				(handler) => Gleap.registerCustomAction(handler),
				() => caseAccountId.current,
				(url) => window.open(url, '_blank', 'noopener,noreferrer'),
			)
			isInitialized.current = true
		}

		const primaryEmail = user.primaryEmailAddress?.emailAddress
		const fullName = user.fullName || `${user.firstName} ${user.lastName}`.trim()
		const fullNameWithOrg = organization?.name
			? `${fullName} (${organization.name})`
			: fullName

		if (!primaryEmail || !fullName) {
			console.warn('Missing essential user data for Gleap chat', {
				primaryEmail,
				fullName,
			})
			return
		}

		const customData = {
			user_id: user.id,
			email: primaryEmail,
			full_name: fullName,
			avatar: user.imageUrl ?? '',
			created_at: user.createdAt?.toISOString() ?? '',
			last_sign_in: user.lastSignInAt?.toISOString() ?? '',
			org_name: organization?.name ?? 'No Organization',
			org_id: organization?.id ?? '',
			account_id: accountInfo?.id ?? '',
			has_2fa: user.twoFactorEnabled,
			total_orgs: user.organizationMemberships?.length ?? 0,
			auth_strategy: user.primaryEmailAddress?.verification?.strategy ?? 'unknown',
			crm: 'v2',
		}

		// Waits for the identity-verification hash: the effect runs again when it arrives.
		identifyWhenVerified(
			Gleap.identify,
			user.id,
			{
				name: fullNameWithOrg,
				email: primaryEmail,
				company: organization ? { id: organization.id, name: organization.name } : undefined,
				customData,
			},
			gleapHash?.userHash,
			() => console.warn('Gleap refused to identify the user'),
		)
	}, [accountInfo, apiKey, user, organization, gleapHash?.userHash])
}
